import { useEffect, useState } from "react";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import queryKeys from "@configs/react-query/query-key";
import { PROGRESS_VERIFICATION_REPORT_STATUS } from "@constants/verificationReport";
import { useNavigate } from "react-router-dom";
import { TOAST } from "@constants/index";
import { useTranslation } from "react-i18next";
import useSuccessToast from "@configs/react-toastify/snackbar";

const useVerificationReportDetail = (verificationReportSeq) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [verificationReportStatus, setVerificationReportStatus] = useState("");
  // Skip license-policy fetch after 404; keep as state so enabled flips in the same render cycle.
  const [licensePolicyNotFound, setLicensePolicyNotFound] = useState(false);

  // Fetch verification report data
  const { data: fetchedVerificationData, error, refetch: refetchVerificationData, isLoading : isLoadingVerificationData } = useFetchData(
    [queryKeys.getVerificationReportByVerificationReportSeq, verificationReportSeq],
    `/verification_report/${verificationReportSeq}`,
    { method: "GET" },
    {},
    { enabled: !!verificationReportSeq },
  );

  useEffect(() => {
    if (error?.response?.status === 404) {
      navigate("/404", { state: { backTo: "/verification-report" } });
    }
  }, [error, navigate]);

  useEffect(() => {
    setLicensePolicyNotFound(false);
  }, [verificationReportSeq]);

  const projectId = fetchedVerificationData?.projectId;
  const scanComponents = fetchedVerificationData?.verificationReportDetails || [];
  const verificationReportProjects = fetchedVerificationData?.verificationReportProjects || [];

  useEffect(() => {
    if (fetchedVerificationData?.verificationReportStatusCd) {
      setVerificationReportStatus(fetchedVerificationData?.verificationReportStatusCd);
    }
  }, [fetchedVerificationData]);

  const { data: fetchedProjectData, isLoading : isLoadingProjectData } = useFetchData(
    [queryKeys.getProjectById, projectId],
    `/project_by_project_id/${projectId}`,
    { method: "GET" },
    {},
    { enabled: !!projectId },
  );

  // Derive seq in-render (not via useEffect) so license-policy loading starts in the same
  // commit as project data — avoids isLoading flickering false→true and remounting ComponentTable.
  const licensePolicySeq =
    licensePolicyNotFound || fetchedProjectData?.licensePolicySeq == null
      ? null
      : fetchedProjectData.licensePolicySeq;

  const { data: fetchedLicensePolicy, isLoading: isLoadingLicensePolicy, error: licensePolicyError } = useFetchData(
    licensePolicySeq != null
      ? [queryKeys.getLicensePolicyByLicensePolicySeq, licensePolicySeq]
      : [queryKeys.getLicensePolicyByLicensePolicySeq, "disabled"],
    `/license_policy/${licensePolicySeq}`,
    { method: "GET" },
    {},
    {
      enabled: licensePolicySeq != null,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (licensePolicyError?.response?.status === 404) {
      console.warn("License policy not found, skipping fetch");
      setLicensePolicyNotFound(true);
    }
  }, [licensePolicyError]);

  const updateVerificationReportStatusToReviewMutation = useMutationData(
    "/verification_report_status",
    { method: "PUT" },
    {
      onSuccess: () => {
        // Show success toast and update local state
        useSuccessToast(TOAST.success, t("common.moved-to-review-successfully"));
        setVerificationReportStatus(PROGRESS_VERIFICATION_REPORT_STATUS.REVIEW);
        refetchVerificationData();
      },
      onError: (error) => {
        console.error("Error updating verification status:", error);
      },
    },
  );

  const updateVerificationReportStatusToDistributeMutation = useMutationData(
    "/verification_report_status",
    { method: "PUT" },
    {
      onSuccess: () => {
        // Show success toast and update local state
        useSuccessToast(TOAST.success, t("common.distributed-successfully"));
        setVerificationReportStatus(PROGRESS_VERIFICATION_REPORT_STATUS.DISTRIBUTION_DONE);
        refetchVerificationData();
      },
      onError: (error) => {
        console.error("Error updating verification status:", error);
      },
    },
  );

  const rejectRequestReviewMutation = useMutationData(
    "/verification_report_status",
    { method: "PUT" },
    {
      onSuccess: () => {
        // Show success toast and update local state
        useSuccessToast(TOAST.success, t("common.rejected-review-successfully"));
        setVerificationReportStatus(PROGRESS_VERIFICATION_REPORT_STATUS.WRITE);
        refetchVerificationData();
      },
      onError: (error) => {
        console.error("Error updating verification status:", error);
      },
    },
  );

  const rejectReReviewMutation = useMutationData(
    "/verification_report_status",
    { method: "PUT" },
    {
      onSuccess: () => {
        // Show success toast and update local state
        useSuccessToast(TOAST.success, t("common.moved-back-to-review-successfully"));
        setVerificationReportStatus(PROGRESS_VERIFICATION_REPORT_STATUS.REVIEW);
        refetchVerificationData();
      },
      onError: (error) => {
        console.error("Error updating verification status:", error);
      },
    },
  );

  const handleMoveToReview = () => {
    updateVerificationReportStatusToReviewMutation.mutate({
      verificationReportSeq: verificationReportSeq,
      verificationReportStatusCd: PROGRESS_VERIFICATION_REPORT_STATUS.REVIEW,
    });
  };

  const handleMoveToDistribute = () => {
    updateVerificationReportStatusToDistributeMutation.mutate({
      verificationReportSeq: verificationReportSeq,
      verificationReportStatusCd: PROGRESS_VERIFICATION_REPORT_STATUS.DISTRIBUTION_DONE,
    });
  };

  const handleRejectRequestReview = () => {
    rejectRequestReviewMutation.mutate({
      verificationReportSeq: verificationReportSeq,
      verificationReportStatusCd: PROGRESS_VERIFICATION_REPORT_STATUS.WRITE,
    });
  };

  const handleRejectReReview = () => {
    rejectReReviewMutation.mutate({
      verificationReportSeq: verificationReportSeq,
      verificationReportStatusCd: PROGRESS_VERIFICATION_REPORT_STATUS.REVIEW,
    });
  };

  const handleClose = () => {
    navigate("/verification-report");
  };

  return {
    fetchedVerificationData,
    fetchedProjectData,
    fetchedLicensePolicy,
    scanComponents,
    verificationReportProjects,
    verificationReportStatus,
    handleMoveToReview,
    handleMoveToDistribute,
    handleRejectRequestReview,
    handleRejectReReview,
    handleClose,
    refetchVerificationData,
    isLoading: isLoadingLicensePolicy || isLoadingProjectData || isLoadingVerificationData,
  };
};

export default useVerificationReportDetail;
